import type { StorageManager } from "@fluid-app/storage-manager";
import { createSession } from "@fluid-app/api-client";
import { STORAGE_KEYS } from "@fluid-app/storage-manager";

import { debug } from "../utils/debug";
import { retry } from "../utils/retry";

/**
 * Manages session operations for the FairShare SDK
 */
export class SessionManager {
  private storageManager: StorageManager;
  private sessionToken: string | null = null;
  private retryCount: number;
  private fluidShop: string;

  /**
   * Create a new SessionManager
   * @param storageManager Storage manager
   * @param fingerPrintManager Fingerprint manager
   * @param retryCount Number of times to retry operations
   * @param fluidShop The Fluid shop identifier
   * @param attributionManager Optional attribution manager for including attribution data
   */
  constructor(storageManager: StorageManager, retryCount = 3, fluidShop = "") {
    this.storageManager = storageManager;
    this.retryCount = retryCount;
    this.fluidShop = fluidShop;
  }

  /**
   * Set the Fluid shop identifier
   * @param fluidShop The Fluid shop identifier
   */
  public setFluidShop(fluidShop: string): void {
    this.fluidShop = fluidShop;
  }

  /**
   * Initialize the session
   * @returns A promise that resolves when the session is initialized
   */
  public async initSession(): Promise<void> {
    try {
      // Try to get the session token (this will now check storage first)
      const session = this.getSessionToken();
      if (session) {
        debug("Using existing session", session);
        return;
      }

      // Create new session
      this.sessionToken = await this.createSession();
      debug("Created new session", this.sessionToken);
    } catch (error: unknown) {
      // Type assertion for error to use in debug
      debug("Error initializing session", error);
    }
  }

  /**
   * Create a new session
   * @returns A promise that resolves with the new session data
   */
  public async createSession(): Promise<string | null> {
    if (!this.fluidShop) {
      throw new Error("fluidShop is required to create a session");
    }

    // Prepare session creation request
    const sessionRequest: {
      fluid_shop: string;
    } = {
      fluid_shop: this.fluidShop,
    };

    // Send session data to server
    try {
      // Wrap the session creation in a try-catch to handle errors
      const response = await retry<{ status: number; fluid_session: string }>(
        async () => {
          const sessionResponse = await createSession({
            request: sessionRequest,
          });
          return sessionResponse;
        },
        this.retryCount,
      );

      debug("Full response from create session:", response);

      // Directly extract and use fluid_session if it exists
      const { fluid_session: sessionToken } = response;

      // Ensure we have a valid string
      if (!sessionToken || typeof sessionToken !== "string") {
        debug("Invalid fluid_session received", sessionToken);
        return null;
      }

      debug("Received session token from server", sessionToken);

      // Store the clean token
      this.storageManager.setItem(STORAGE_KEYS.SESSION, sessionToken);
      this.setSessionToken(sessionToken);

      return sessionToken;
    } catch (error: unknown) {
      debug("Error creating session on server", error);
      return null;
    }
  }

  /**
   * Get the current session ID
   * @returns The current session ID or null if no session exists
   */
  public getSessionToken(): string | null {
    // If we already have a session token in memory, return it
    if (this.sessionToken) {
      debug("Using session token from memory:", this.sessionToken);
      return this.sessionToken;
    }

    // If we don't have a session token in memory, try to get it from storage
    try {
      const storedSession = this.storageManager.getItem(STORAGE_KEYS.SESSION);

      if (storedSession) {
        debug("Retrieved session token from storage:", storedSession);
        // Important: Set the token in memory to avoid repeated lookups
        this.sessionToken = storedSession;
        return storedSession;
      }

      debug("No session token found in storage");
      return null;
    } catch (error: unknown) {
      debug("Error retrieving session token from storage:", error);
      // Only return null if we truly failed to retrieve a token
      return null;
    }
  }

  /**
   * Set the current session data
   * @param sessionToken The session data to set
   */
  public setSessionToken(sessionToken: string | null): void {
    this.sessionToken = sessionToken;
  }
}
